import { Appointment, TreatmentStep } from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  IAppointment,
  IBrand,
  IOrganisation,
  PatientCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  collectionGroupQuery,
  orderBy,
  toTimestamp,
  where,
  type Query,
  type WithRef,
} from '@principle-theorem/shared';
import { BigQueryJobName } from '../../job-names';
import { IBaseAppointmentTopicData } from '../../topic-data/base-topic-data';
import {
  BasicBigQuerySyncHandler,
  IBigQuerySyncRequest,
} from '../big-query-sync';

export class BigQueryAppointmentSyncHandler extends BasicBigQuerySyncHandler<
  IAppointment,
  IBaseAppointmentTopicData
> {
  readonly bigQueryJobName = BigQueryJobName.AppointmentWrite;

  getQuery(request: IBigQuerySyncRequest): Query<IAppointment> {
    return collectionGroupQuery<IAppointment>(
      PatientCollection.Appointments,
      where('event.practice.ref', '==', request.practiceRef),
      where('event.from', '>=', request.range.from),
      where('event.from', '<=', request.range.to),
      orderBy('event.from')
    );
  }

  async getTopicData(
    appointment: WithRef<IAppointment>
  ): Promise<IBaseAppointmentTopicData> {
    const patientRef = Appointment.patientRef(appointment);
    const brandRef = Firestore.getParentDocRef<IBrand>(patientRef);
    const organisationRef = Firestore.getParentDocRef<IOrganisation>(brandRef);
    const treatmentCategory = await TreatmentStep.resolveTreatmentCategory(
      appointment.treatmentPlan.treatmentStep.display
    );
    const appointmentRequest = appointment.appointmentRequestRef
      ? await Firestore.getDoc(appointment.appointmentRequestRef)
      : undefined;

    return {
      organisation: await Firestore.getDoc(organisationRef),
      brand: await Firestore.getDoc(brandRef),
      practice: await Firestore.getDoc(appointment.practice.ref),
      patient: await Firestore.getDoc(patientRef),
      appointment,
      treatmentCategory,
      appointmentRequest,
      timestamp: toTimestamp(),
    };
  }
}

export class BigQueryUnscheduledAppointmentSyncHandler extends BigQueryAppointmentSyncHandler {
  override getQuery(request: IBigQuerySyncRequest): Query<IAppointment> {
    return collectionGroupQuery<IAppointment>(
      PatientCollection.Appointments,
      where('practice.ref', '==', request.practiceRef),
      where('status', '==', AppointmentStatus.Unscheduled)
    );
  }
}
